import {
  describeStageBasis,
  insurancePerDayAmount,
  resolvePricingStages,
  type PricingBreakdown,
  type PricingStage,
  type PricingStageKey,
} from "@fm/api";
import { formatDateShort } from "@fm/ui";
import { Lock } from "lucide-react";

/**
 * One pricing stage — its label, its items and its total.
 *
 * A percentage item shows the base it resolved against, because a bare amount
 * makes a stacked 8.41% and 5% look arbitrary on the invoice.
 */
const StageRows = ({
  stage,
  formatCurrency,
  tone = "default",
}: {
  stage: PricingStage;
  formatCurrency: (amount: string | number) => string;
  /**
   * `credit` renders the amounts in a positive tone for a stage that takes
   * money off (a discount) instead of adding it. The amounts are already
   * negative, so the minus sign comes from the formatter — never negate again.
   * Deliberately not red: a credit is good news, not an error.
   */
  tone?: "default" | "credit";
}) => (
  <div className="pt-4 border-t border-gray-200">
    <div className="flex items-center justify-between mb-2">
      <span className="font-medium text-gray-900">{stage.label}</span>
      <span
        className={`font-semibold ${
          tone === "credit" ? "text-emerald-700" : "text-gray-900"
        }`}
      >
        {formatCurrency(stage.total)}
      </span>
    </div>
    <div className="ml-4 space-y-1">
      {stage.items.map((item, index) => {
        const basis = describeStageBasis(item, formatCurrency);

        return (
          <div
            key={item.metadata?.fee_public_id ?? `${stage.key}-${index}`}
            className="flex items-center justify-between text-sm text-gray-600"
          >
            <span>
              {item.description}
              {basis && (
                <span className="text-gray-400"> &middot; {basis}</span>
              )}
            </span>
            <span
              className={tone === "credit" ? "text-emerald-700" : undefined}
            >
              {formatCurrency(item.total_amount)}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

export interface PricingBreakdownDisplayProps {
  breakdown: PricingBreakdown | null;
  isLoading?: boolean;
  className?: string;
  /**
   * Optional security deposit amount to render as a held line below the total.
   * Informational only — never added to the charged total. Pass `null` or
   * `undefined` to hide the line entirely.
   */
  securityDeposit?: number | null;
}

/**
 * Component to display detailed pricing breakdown
 *
 * Shows:
 * - Base rental (daily breakdown)
 * - Add-ons (with individual prices)
 * - Delivery fee (with distance if applicable)
 * - Total
 *
 * @example
 * ```tsx
 * <PricingBreakdownDisplay
 *   breakdown={pricingBreakdown}
 *   isLoading={isCalculating}
 * />
 * ```
 */
export function PricingBreakdownDisplay({
  breakdown,
  isLoading = false,
  className = "",
  securityDeposit,
}: PricingBreakdownDisplayProps) {
  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return `$${num.toFixed(2)}`;
  };

  const formatDate = (dateStr: string) => formatDateShort(dateStr);

  if (isLoading) {
    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!breakdown) {
    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}
      >
        <p className="text-center text-gray-500">
          No pricing information available
        </p>
      </div>
    );
  }

  const { total, currency, breakdown: details } = breakdown;
  const {
    base_rental,
    addons,
    insurance_options,
    insurance,
    delivery,
    subtotal,
    custom_charges,
    discounts,
  } = details;

  const stages = resolvePricingStages(details);

  // Normalize insurance items (backend might return 'insurance' or 'insurance_options')
  const insuranceItems = insurance_options || insurance || [];

  // Calculate addons subtotal
  const addonsSubtotal = addons.reduce(
    (sum, addon) => sum + parseFloat(addon.total),
    0,
  );

  // Calculate insurance subtotal
  const insuranceSubtotal = insuranceItems.reduce(
    (sum: number, option: { total: string }) => sum + parseFloat(option.total),
    0,
  );

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Pricing Breakdown
        </h3>
      </div>

      <div className="p-4 space-y-4">
        {/* Base Rental */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-gray-900">Base Rental</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(base_rental.total)}
            </span>
          </div>

          {/* Daily Rates */}
          {base_rental.daily_rates && base_rental.daily_rates.length > 0 && (
            <div className="ml-4 space-y-1">
              {base_rental.daily_rates.map((rate, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm text-gray-600"
                >
                  <span>{formatDate(rate.date)}</span>
                  <span>{formatCurrency(rate.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add-ons */}
        {addons && addons.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Add-ons</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(addonsSubtotal)}
              </span>
            </div>

            <div className="ml-4 space-y-1">
              {addons.map((addon, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex-1">
                    <div className="text-gray-900">{addon.name}</div>
                    {addon.quantity > 1 && (
                      <div className="text-xs text-gray-500">
                        {addon.quantity} x {formatCurrency(addon.unit_price)}
                      </div>
                    )}
                  </div>
                  <span className="text-gray-600 ml-2">
                    {formatCurrency(addon.total)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insurance Options */}
        {insuranceItems && insuranceItems.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Insurance</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(insuranceSubtotal)}
              </span>
            </div>

            <div className="ml-4 space-y-1">
              {insuranceItems.map((option: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex-1">
                    <div className="text-gray-900">{option.name}</div>
                    {/*
                      Only a per-day policy has a daily rate. This line used to
                      render unconditionally, so a one-time $75.00 premium was
                      labelled "$75.00 per day". The multiplier mirrors the
                      add-on row above: it is what lets the customer check the
                      total.
                    */}
                    {option.unit === "per_day" && base_rental?.days ? (
                      <div className="text-xs text-gray-500">
                        {formatCurrency(
                          insurancePerDayAmount(option, base_rental.days),
                        )}
                        /day × {base_rental.days}
                      </div>
                    ) : null}
                  </div>
                  <span className="text-gray-600 ml-2">
                    {formatCurrency(option.total)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery */}
        {delivery && parseFloat(delivery.total) > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Delivery</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(delivery.total)}
              </span>
            </div>

            <div className="ml-4 space-y-1 text-sm text-gray-600">
              {delivery.base_fee && parseFloat(delivery.base_fee) > 0 && (
                <div className="flex items-center justify-between">
                  <span>Base fee</span>
                  <span>{formatCurrency(delivery.base_fee)}</span>
                </div>
              )}

              {delivery.distance_fee &&
                parseFloat(delivery.distance_fee) > 0 && (
                  <div className="flex items-center justify-between">
                    <span>
                      Distance fee
                      {delivery.distance && (
                        <span className="text-xs ml-1">
                          ({delivery.distance.toFixed(2)} mi)
                        </span>
                      )}
                    </span>
                    <span>{formatCurrency(delivery.distance_fee)}</span>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Custom Charges. Operator-authored, already counted inside the
            subtotal — same as fees_subtotal, so it reuses that section's
            rendering. The stage key here is a display-only label, never
            resolved through resolvePricingStages, so it is safe to cast. */}
        {custom_charges && custom_charges.items.length > 0 && (
          <StageRows
            stage={{
              key: "custom_charges" as PricingStageKey,
              label: "Custom Charges",
              total: custom_charges.total,
              items: custom_charges.items,
            }}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Discounts. Credits against the base rental, already counted inside
            the subtotal, so they render just above it and just after the
            custom charges. Their amounts are already negative — the formatter
            supplies the minus sign, and the credit tone keeps them from
            reading as an error. The stage key here is a display-only label,
            never resolved through resolvePricingStages, so it is safe to
            cast. */}
        {discounts && discounts.items.length > 0 && (
          <StageRows
            stage={{
              key: "discounts" as PricingStageKey,
              label: "Discounts",
              total: discounts.total,
              items: discounts.items,
            }}
            formatCurrency={formatCurrency}
            tone="credit"
          />
        )}

        {/* Fixed-amount fees, rendered above the Subtotal because they are
            already counted inside it. */}
        {stages.beforeSubtotal.map((stage) => (
          <StageRows
            key={stage.key}
            stage={stage}
            formatCurrency={formatCurrency}
          />
        ))}

        {/* Subtotal — shown whenever anything is charged on top of it, not
            only when there are taxes. */}
        {stages.hasStages && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">Subtotal</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(subtotal)}
              </span>
            </div>
          </div>
        )}

        {/* Pre-tax fees, then tax, then post-tax fees. The order is the
            business rule, not presentation. */}
        {stages.afterSubtotal.map((stage) => (
          <StageRows
            key={stage.key}
            stage={stage}
            formatCurrency={formatCurrency}
          />
        ))}

        {/* Total */}
        <div className="pt-4 border-t-2 border-gray-300">
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-gray-900">Total</span>
            <span className="text-2xl font-bold text-gray-900">
              {formatCurrency(total)}{" "}
              <span className="text-sm font-normal text-gray-600">
                {currency}
              </span>
            </span>
          </div>
        </div>

        {securityDeposit !== null &&
          securityDeposit !== undefined &&
          securityDeposit > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm italic text-gray-500">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  <span>Security Deposit (hold)</span>
                </div>
                <span>{formatCurrency(securityDeposit)}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1 ml-6">
                Held, not charged — released after the rental ends
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
